// src/data/officeAtAct.js
//
// WO-001 S8 (2026-09-14, task list section 246): "who held this office when this claim was acted on,"
// on the claim record. Design SS6 of data\YCP_OFFICE_SUCCESSION_DESIGN_2026-08-17.md - the part that
// hurts at handover is that a claim acted on last March by the chairman of last March reads, a year
// later, as if the chairman of today did it. The claim's history rows (claims/{id}/history, written by
// the claimAuditHistory trigger) each carry a timestamp and an actor; the office spine (offices/,
// officeTerms/) carries who held each office on any date. This module joins the two and says, for
// every act: who held the General office and the Local seat THAT DAY, and whether the person who
// acted WAS the holder of record - or somebody else (the member on his own claim, the administrator,
// or - the case this exists to catch - an account whose token said 'general_chair' on a day the term
// record says he was not).
//
// Pure: no React, no Firestore. The caller reads the rows, the two offices and their terms, and hands
// them in. Nothing here invents a date: an act's date is its timestamp; an office with no term on that
// date reads VACANT or not of record, as officeStateOn says.
import { officeStateOn, holderLine } from './officeSuccession';

export const ACTOR_SEATS = {
  GC_OF_RECORD: 'general_chair_of_record',
  LC_OF_RECORD: 'local_chair_of_record',
  ADMIN: 'administrator',
  MEMBER: 'member',
  SYSTEM: 'system',
  NOT_OF_RECORD: 'officer_not_of_record',   // the flag
  UNKNOWN: 'unknown',
};

const dayOf = (ts) => (typeof ts === 'string' && ts.length >= 10) ? ts.slice(0, 10) : null;

// One act. `ctx`: { gcOffice, lcOffice, terms } - the offices as offices/ holds them (or null when not
// seeded), every term of both. Returns what the row should say.
export function officeAtAct(entry, ctx) {
  const c = ctx || {};
  const e = entry || {};
  const date = dayOf(e.timestamp);
  if (!date) return { date: null, gc: null, lc: null, actorSeat: ACTOR_SEATS.UNKNOWN, flag: 'the act carries no timestamp - its office of record cannot be read', gcLine: '', lcLine: '' };
  const gc = c.gcOffice ? officeStateOn(c.gcOffice, c.terms, date) : null;
  const lc = c.lcOffice ? officeStateOn(c.lcOffice, c.terms, date) : null;
  const uid = e.actor && e.actor !== 'unknown' ? String(e.actor) : null;
  const role = e.actorRole || null;
  let actorSeat = ACTOR_SEATS.UNKNOWN, flag = null;
  if (gc && gc.state === 'held' && uid && gc.term.holderUid === uid) actorSeat = ACTOR_SEATS.GC_OF_RECORD;
  else if (lc && lc.state === 'held' && uid && lc.term.holderUid === uid) actorSeat = ACTOR_SEATS.LC_OF_RECORD;
  else if (role === 'admin') actorSeat = ACTOR_SEATS.ADMIN;
  else if (role === 'member' || role === 'claimant') actorSeat = ACTOR_SEATS.MEMBER;
  else if (role === 'system' || uid === 'system') actorSeat = ACTOR_SEATS.SYSTEM;
  else if (role === 'general_chair' || role === 'local_chair') {
    // The token said officer; the term record, on that date, does not name him. That is either a
    // term not yet attested (ruling 54 - not of record before launch) or an act by someone whose
    // authority the spine never recorded. Both are said plainly; neither is smoothed over.
    const which = role === 'general_chair' ? gc : lc;
    actorSeat = ACTOR_SEATS.NOT_OF_RECORD;
    flag = (e.actorName || uid || 'the actor') + ' acted as ' + (role === 'general_chair' ? 'General Chairperson' : 'Local Chairperson') + ' on ' + date + ', and the term record '
      + (!which ? 'has no office to check against' : which.state === 'held' ? 'names ' + which.term.holderName + ' as the holder that day' : which.state === 'vacant' ? 'says the office was VACANT that day' : 'carries no holder for that seat (not of record)')
      + ' - the act stands as recorded; the seat of the person who made it is not established by the spine.';
  }
  return {
    date, gc, lc, actorSeat, flag,
    gcLine: c.gcOffice ? holderLine(c.gcOffice, c.terms, date) : 'General office not on record',
    lcLine: c.lcOffice ? holderLine(c.lcOffice, c.terms, date) : (c.scopeKey ? 'Local seat ' + c.scopeKey + ' not on record' : 'no Local seat on the claim'),
  };
}

export function actorSeatLabel(seat) {
  switch (seat) {
    case ACTOR_SEATS.GC_OF_RECORD: return 'General Chairperson of record that day';
    case ACTOR_SEATS.LC_OF_RECORD: return 'Local Chairperson of record that day';
    case ACTOR_SEATS.ADMIN: return 'platform administrator';
    case ACTOR_SEATS.MEMBER: return 'the member';
    case ACTOR_SEATS.SYSTEM: return 'the platform';
    case ACTOR_SEATS.NOT_OF_RECORD: return 'officer - NOT the holder of record that day';
    default: return 'seat not established';
  }
}

// Every act, annotated; plus the count of flagged ones so a page can say it once at the top.
export function annotateHistory(rows, ctx) {
  const list = Array.isArray(rows) ? rows : [];
  const acts = list.map(r => ({ ...r, office: officeAtAct(r, ctx) }));
  const flagged = acts.filter(a => a.office.flag).length;
  // The offices that held the claim across its life: one line per distinct holder, in order.
  const holders = [];
  for (const a of acts.slice().reverse()) {   // rows arrive newest first; read oldest first
    for (const k of ['gc', 'lc']) {
      const st = a.office[k];
      if (!st || st.state !== 'held') continue;
      const key = k + ':' + st.term.termId;
      if (!holders.some(h => h.key === key)) holders.push({ key, kind: k, termId: st.term.termId, holderName: st.term.holderName, acting: st.acting, firstActOn: a.office.date });
    }
  }
  return { acts, flagged, holders };
}
